import { OperationType } from "@/api/model";

/**
 * Options for the GSD V4 "Operation Type" dropdown.
 *
 * The values are the backend `OperationType` enum (flowed to the frontend via the generated
 * API client), so adding a new enum value + regenerating the client is all that is needed.
 * The display names are defined here because they are not part of the enum. Order and default
 * ("Sew") match the GSDCOST dropdown.
 */
export const OPERATION_TYPE_OPTIONS: { value: OperationType; label: string }[] = [
  { value: OperationType.Bh, label: "Button Hole" },
  { value: OperationType.Bt, label: "Bartack" },
  { value: OperationType.Bs, label: "Button Sew" },
  { value: OperationType.Ac, label: "Auto Cycle" },
  { value: OperationType.Sew, label: "Sew" }
];

/**
 * Numeric index of each `OperationType`, matching the backend enum declaration order
 * (BS = 0, BT = 1, BH = 2, AC = 3, Sew = 4).
 */
const OPERATION_TYPE_BY_NUMERIC: Record<number, OperationType> = {
  0: OperationType.Bs,
  1: OperationType.Bt,
  2: OperationType.Bh,
  3: OperationType.Ac,
  4: OperationType.Sew
};

/**
 * Normalizes an `operationType` value coming from the API to the string enum value the
 * "Operation Type" ComboBox binds to. The API serializes this enum as its integer value
 * (e.g. Sew -> 4), which does not match the string-keyed dropdown options and would render
 * as the raw number. Accepts a number, a numeric string, or an already-correct string;
 * returns null for empty/unknown values so callers can apply their own default.
 */
export function normalizeOperationType(value: unknown): OperationType | null {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }
  if (value === "") {
    return null;
  }
  const key = String(value).trim();
  // Matches a numeric enum (e.g. 4) or a string enum (e.g. "Sew"); Number("Sew") is NaN and misses the map.
  return (
    OPERATION_TYPE_BY_NUMERIC[Number(key)] ?? OPERATION_TYPE_OPTIONS.find(option => option.value === key)?.value ?? null
  );
}
